/**
 * GA4 Custom Events — Theme-side publisher
 * =========================================
 * Custom events NOT covered by Shopify Web Pixels API natively.
 *
 * Architecture (single source):
 *   Theme → Shopify.analytics.publish('custom_*', data)
 *        → Custom Pixel subscribes via analytics.subscribe('custom_*', ...)
 *        → Pixel builds payload → postMessage → dataLayer → GTM
 *
 * Events published (via Shopify.analytics.publish → pixel → dataLayer):
 *   select_item, filter_applied, sort_applied, add_to_wishlist
 *
 * Events pushed directly to dataLayer (theme-only, pixel can't detect):
 *   view_cart (cart drawer open)
 *   add_to_wishlist (Swym Wishlist Plus bridge)
 *   add_to_wishlist (Swym Wishlist Plus bridge)
 *
 * Excluded from scope:
 *   sign_up, login, logout — Shopify non permette tracciamento via Web Pixel
 */

window.GA4 = window.GA4 || {};

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // TRANSPORT — single path: Shopify.analytics.publish → pixel → postMessage → dataLayer
  // ---------------------------------------------------------------------------
  function publishToShopify(eventName, data) {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('custom_' + eventName, data);
    }
  }

  // ---------------------------------------------------------------------------
  // PUBLIC API — publish only (no direct dataLayer push for custom events)
  // The Custom Pixel (pixel-ga4.js) builds the final GA4 payload and sends it
  // via postMessage → bridge → dataLayer
  // ---------------------------------------------------------------------------
  window.GA4.selectItem = function (product) {
    publishToShopify('select_item', product);
  };

  window.GA4.filterApplied = function (filterKey, filterValue) {
    publishToShopify('filter_applied', {
      filter_key: filterKey || '',
      filter_value: filterValue || '',
    });
  };

  window.GA4.sortApplied = function (sortBy) {
    publishToShopify('sort_applied', {
      sort_by: sortBy || '',
    });
  };

  // ---------------------------------------------------------------------------
  // AUTO-DETECT: Search result / collection / recommendation clicks → select_item
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href*="/products/"]');
    if (!link) return;

    var isSearchResult = link.closest('.search__results') || link.closest('.predictive-search');
    var isCollection = link.closest('[data-collection]') || link.closest('.collection');
    var isRecommendation = link.closest('.product-recommendations');

    var listId = '';
    var listName = '';

    if (isSearchResult) {
      listName = 'search_results';
    } else if (isCollection) {
      var collEl = link.closest('[data-collection]');
      listId = (collEl && collEl.dataset.collectionId) || '';
      listName = (collEl && collEl.dataset.collection) || 'collection';
    } else if (isRecommendation) {
      listName = 'recommended_products';
    } else {
      return;
    }

    var card = link.closest('[data-product-id]') || link.closest('.product-card');
    var index = 1;
    if (card) {
      var siblings = card.parentNode ? card.parentNode.querySelectorAll('.product-card, [data-product-id]') : [];
      for (var s = 0; s < siblings.length; s++) {
        if (siblings[s] === card) { index = s + 1; break; }
      }
    }
    var productData = {
      item_id: card ? card.dataset.productId : '',
      item_name: card ? (card.dataset.productTitle || (card.querySelector('.product-card__title') ? (card.querySelector('.product-card__title') || {}).textContent : '')) : '',
      item_list_id: listId,
      item_list_name: listName,
      index: index,
    };

    window.GA4.selectItem(productData);
  });

  // ---------------------------------------------------------------------------
  // CART DRAWER — view_cart on open (no page reload, Web Pixel doesn't fire)
  // ---------------------------------------------------------------------------
  document.addEventListener('drawer:handleAfterShow', function (e) {
    var cartDrawer = e.target.closest('cart-drawer');
    if (!cartDrawer) return;

    var ctx = window.__GA4_USER_CONTEXT__ || { user_id: '', login_status: 'logged_out' };
    var rootUrl =
      (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.root_url) || '/';
    var cartUrl = rootUrl.replace(/\/$/, '') + '/cart.js';

    fetch(cartUrl)
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed');
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) return;

        var items = (cart.items || []).map(function (item, i) {
          var priceCents = Number(item.final_price || item.price || 0);
          return {
            item_id: String(item.variant_id || item.product_id || ''),
            item_name: item.product_title || item.title || '',
            item_brand: item.vendor || '',
            item_category: item.product_type || '',
            item_variant:
              (item.variant_title && item.variant_title !== 'Default Title'
                ? item.variant_title
                : '') ||
              (item.variant_options ? item.variant_options.join(' - ') : ''),
            price: priceCents / 100,
            quantity: item.quantity || 1,
            index: i + 1,
          };
        });

        var value = items.reduce(function (sum, it) {
          return sum + it.price * it.quantity;
        }, 0);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_cart',
          user_id: ctx.user_id,
          login_status: ctx.login_status,
          ecommerce: {
            affiliation: 'Beauty Star',
            currency: cart.currency || 'EUR',
            value: value,
            items: items,
          },
        });
      })
      .catch(function (err) {
        console.warn('[GA4] Failed to push view_cart from drawer:', err.message);
      });
  });

  // ---------------------------------------------------------------------------
  // WISHLIST — add_to_wishlist bridge
  // WishlistButton (theme.js) dispatches `wishlist:added` after swat.addToList.
  // We read product data from the custom element and publish through the
  // Shopify Web Pixels sandbox (same path as select_item/filter_applied).
  // ---------------------------------------------------------------------------
  document.addEventListener('wishlist:added', function (e) {
    var variantId = String(e.detail && e.detail.variantId || '');
    var productId = String(e.detail && e.detail.productId || '');

    var wb = document.querySelector('wishlist-button[data-product-id="' + productId + '"]');
    if (!wb) return;

    var itemName = wb.dataset.productTitle || '';
    var itemBrand = wb.dataset.productVendor || '';
    var itemCategory = wb.dataset.productType || '';
    var itemVariant = wb.dataset.productVariant || '';
    if (itemVariant === 'Default Title') itemVariant = '';
    var priceCents = parseInt(wb.dataset.productPrice, 10) || 0;

    publishToShopify('add_to_wishlist', {
      variant_id: variantId,
      product_id: productId,
      item_name: itemName,
      item_brand: itemBrand,
      item_category: itemCategory,
      item_variant: itemVariant,
      price_cents: priceCents,
    });
  });

})();
